import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme as useReduxTheme } from '../redux/hooks/useTheme';
import { themeConfig } from './theme';

// Main theme hook that combines Redux and MUI theme
export const useAppTheme = () => {
  const themeState = useReduxTheme();
  const muiTheme = useMuiTheme();

  return {
    ...themeState,
    theme: muiTheme,
  };
};

// Hook to get current theme colors
export const useThemeColors = () => {
  const { themeMode } = useReduxTheme();
  const muiTheme = useMuiTheme();

  const currentPalette = themeMode === 'dark' 
    ? themeConfig.palette.dark 
    : themeConfig.palette.light;

  return {
    // Brand colors
    primary: themeConfig.palette.primary,
    secondary: themeConfig.palette.secondary,
    success: themeConfig.palette.success,
    warning: themeConfig.palette.warning,
    error: themeConfig.palette.error,
    info: themeConfig.palette.info,
    
    // Background colors
    background: currentPalette.background,
    
    // Text colors
    text: currentPalette.text,
    
    // Action colors
    action: currentPalette.action,
    
    // Divider color
    divider: currentPalette.divider,
    
    // Gradient colors
    gradient: currentPalette.gradient,
    
    // Scrollbar colors
    scrollbar: currentPalette.scrollbar,
    
    // Surface colors
    surface: currentPalette.surface,
    
    // Accent colors
    accent: currentPalette.accent,
    
    // Shadow colors
    shadow: currentPalette.shadow,
    
    // Grey scale
    grey: themeConfig.palette.grey,
    
    // Current theme mode colors
    mode: {
      background: currentPalette.background,
      text: currentPalette.text,
      action: currentPalette.action,
      divider: currentPalette.divider,
      gradient: currentPalette.gradient,
      scrollbar: currentPalette.scrollbar,
      surface: currentPalette.surface,
      accent: currentPalette.accent,
      shadow: currentPalette.shadow,
    },
  };
};

// Hook to get current theme mode
export const useThemeMode = () => {
  const themeState = useReduxTheme();
  
  return {
    mode: themeState.themeMode,
    isDark: themeState.isDark,
    isLight: themeState.isLight,
    toggle: themeState.toggleTheme,
    setMode: themeState.setTheme,
  };
};

// Hook to get theme spacing
export const useThemeSpacing = () => {
  const muiTheme = useMuiTheme();
  return muiTheme.spacing;
};

// Hook to get theme typography
export const useThemeTypography = () => {
  const muiTheme = useMuiTheme();
  return muiTheme.typography;
};

// Hook to get theme breakpoints
export const useThemeBreakpoints = () => {
  const muiTheme = useMuiTheme();
  return muiTheme.breakpoints;
};

// Hook to get theme shadows
export const useThemeShadows = () => {
  const muiTheme = useMuiTheme();
  return muiTheme.shadows;
};

// Hook to get scrollbar styles
export const useScrollbarStyles = () => {
  const colors = useThemeColors();
  
  return {
    // Webkit scrollbar styles
    webkitScrollbar: {
      width: '8px',
      height: '8px',
    },
    webkitScrollbarTrack: {
      background: colors.scrollbar.track,
      borderRadius: '4px',
    },
    webkitScrollbarThumb: {
      background: colors.scrollbar.thumb,
      borderRadius: '4px',
      '&:hover': {
        background: colors.scrollbar.thumbHover,
      },
    },
    webkitScrollbarCorner: {
      background: colors.scrollbar.corner,
    },
    // Firefox scrollbar styles
    firefoxScrollbar: {
      scrollbarWidth: 'thin',
      scrollbarColor: `${colors.scrollbar.thumb} ${colors.scrollbar.track}`,
    },
    // Combined styles for use in sx prop
    sx: {
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: colors.scrollbar.track,
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: colors.scrollbar.thumb,
        borderRadius: '4px',
        '&:hover': {
          background: colors.scrollbar.thumbHover,
        },
      },
      '&::-webkit-scrollbar-corner': {
        background: colors.scrollbar.corner,
      },
      scrollbarWidth: 'thin',
      scrollbarColor: `${colors.scrollbar.thumb} ${colors.scrollbar.track}`,
    },
  };
};
